import {
  ActionIcon,
  ColorPicker,
  Group,
  Popover,
  ScrollArea,
  Select,
  Slider,
  Stack,
  Text,
  TextInput,
  Textarea,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import { Palette } from "lucide-react";
import { useState, type ReactNode } from "react";

import type {
  AiActorAge,
  AiActorBackground,
  AiActorBody,
  AiActorBuilder,
  AiActorClothingStyle,
  AiActorEthnicity,
  AiActorEyes,
  AiActorFace,
  AiActorFraming,
  AiActorGender,
  AiActorHairColor,
  AiActorHairLength,
  AiActorLighting,
  AiActorShotAngle,
} from "../../../api/generated/model";
import { ImageActorQuote, ImageActorRequest } from "../../../api/generated/zod";
import { ErrorAlert } from "../../ErrorAlert";
import { usePricedStart } from "../../jobs/priced-start";
import { GlassPanel } from "../../../ui/GlassPanel";
import { ModelPicker } from "../../../ui/ModelPicker";
import { Price } from "../../../ui/Price";
import {
  AGES,
  ANGLES,
  BACKGROUNDS,
  BODIES,
  CLOTHING,
  ETHNICITIES,
  EYES,
  FACES,
  FRAMINGS,
  HAIR_COLORS,
  HAIR_LENGTHS,
  LIGHTINGS,
  SEATS,
  type Option,
} from "./builder-options";
import classes from "./composer.module.css";
import {
  GenderPills,
  MAX_ACTORS,
  OutputsChoice,
  defaultActorModel,
  type ActorFormProps,
} from "./GenerateWithText";
import { StartActions } from "./place";



/** The builder's choices, every one unset at first but the gender, as live's form. */
interface Draft {
  gender: AiActorGender;
  age: AiActorAge | null;
  ethnicity: AiActorEthnicity | null;
  ethnicityOther: string;
  body: AiActorBody | null;
  face: AiActorFace | null;
  eyes: AiActorEyes | null;
  eyesCustom: string | null;
  hairLength: AiActorHairLength | null;
  hairColor: AiActorHairColor | null;
  hairColorCustom: string | null;
  clothing: AiActorClothingStyle | null;
  framing: AiActorFraming | null;
  angle: AiActorShotAngle | null;
  seated: boolean | null;
  lighting: AiActorLighting | null;
  lightingOther: string;
  background: AiActorBackground | null;
  description: string;
}

const START: Draft = {
  gender: "female",
  age: null,
  ethnicity: null,
  ethnicityOther: "",
  body: null,
  face: null,
  eyes: null,
  eyesCustom: null,
  hairLength: null,
  hairColor: null,
  hairColorCustom: null,
  clothing: null,
  framing: null,
  angle: null,
  seated: null,
  lighting: null,
  lightingOther: "",
  background: null,
  description: "",
};

/** The request's builder: every unset choice null, as live sends it. */
function builderOf(draft: Draft): AiActorBuilder {
  const text = (value: string) => (value.trim() === "" ? null : value.trim());
  return {
    appearance: {
      age: draft.age,
      ethnicity: draft.ethnicity,
      ethnicity_other: draft.ethnicity === "other" ? text(draft.ethnicityOther) : null,
      body: draft.body,
      face: draft.face,
      eyes: draft.eyes,
      eyes_custom: draft.eyes === "custom" ? draft.eyesCustom : null,
      hair: {
        length: draft.hairLength,
        color: draft.hairColor,
        color_custom: draft.hairColor === "custom" ? draft.hairColorCustom : null,
      },
    },
    clothing: { style: draft.clothing },
    setup: {
      framing: draft.framing,
      shot_angle: draft.angle,
      is_seated: draft.seated,
      lighting: draft.lighting,
      lighting_other: draft.lighting === "other" ? text(draft.lightingOther) : null,
      background: draft.background,
      background_description: text(draft.description),
    },
  };
}

/** A section's heading, live's small capitals ("GENDER", "FRAMING & ANGLE"). */
function Label({ children }: { children: ReactNode }) {
  return <Text className={classes.label}>{children}</Text>;
}

/** Live's cards of one choice: one at a time, and a second click on it lets it go. */
function Cards<T extends string>(props: {
  label: string;
  options: readonly Option<T>[];
  value: T | null;
  onChange: (value: T | null) => void;
}) {
  return (
    <fieldset className={classes.cards} aria-label={props.label}>
      {props.options.map((each) => (
        <UnstyledButton
          key={each.value}
          className={classes.card}
          data-on={props.value === each.value || undefined}
          aria-pressed={props.value === each.value}
          onClick={() => props.onChange(props.value === each.value ? null : each.value)}
        >
          {each.label}
        </UnstyledButton>
      ))}
    </fieldset>
  );
}

/** Live's color dots and its "Custom" picker: a dot, or a color of one's own. */
function Colors<T extends string>(props: {
  label: string;
  token: string;
  options: readonly Option<T>[];
  value: T | "custom" | null;
  custom: string | null;
  onChange: (value: T | null) => void;
  onCustom: (hex: string) => void;
}) {
  return (
    <fieldset className={classes.dots} aria-label={props.label}>
      {props.options.map((each) => (
        <Tooltip key={each.value} label={each.label} withArrow>
          <UnstyledButton
            className={classes.dot}
            aria-pressed={props.value === each.value}
            aria-label={each.label}
            data-on={props.value === each.value || undefined}
            onClick={() => props.onChange(props.value === each.value ? null : each.value)}
          >
            <span
              className={classes.dotFill}
              style={{ backgroundColor: `var(--cai-${props.token}-${each.value})` }}
            />
          </UnstyledButton>
        </Tooltip>
      ))}
      <Popover position="top" withArrow shadow="md">
        <Popover.Target>
          <Tooltip label="Custom" withArrow>
            <ActionIcon
              variant={props.value === "custom" ? "light" : "subtle"}
              radius="xl"
              aria-label={`Custom ${props.label.toLowerCase()} color`}
            >
              <Palette size={18} />
            </ActionIcon>
          </Tooltip>
        </Popover.Target>
        <Popover.Dropdown>
          <ColorPicker
            format="hex"
            {...(props.custom === null ? {} : { value: props.custom })}
            onChange={props.onCustom}
          />
        </Popover.Dropdown>
      </Popover>
    </fieldset>
  );
}

/**
 * Live's AI Actor Builder (`ai-actor-builder`, captures 04 and 05, G8-actor-generation): two
 * columns, "Character and style" (gender, age, ethnicity, body type, face, eyes, hair, clothing)
 * and "Scene and style" (framing and angle, in seat, lighting, background, description); every
 * choice unset at first but the gender, Female. The footer is the model, the count, the price
 * and the start (Talking Head's: one portrait, Cancel and "Generate AI Actor"): the builder
 * writes the prompt, so the price shows at once, as live's 64.
 */
export function ActorBuilder(props: ActorFormProps) {
  const { teamId, brandId, models, above, onStarted, place, inline } = props;
  const [modelId, setModelId] = useState(() => defaultActorModel(models).id);
  const model = models.find((each) => each.id === modelId) ?? defaultActorModel(models);
  const most = Math.min(MAX_ACTORS, model.max_outputs);
  const [outputs, setOutputs] = useState(most);
  const [draft, setDraft] = useState<Draft>(START);
  const [collapsed, setCollapsed] = useState(false);
  const set = (change: Partial<Draft>) => setDraft((current) => ({ ...current, ...change }));
  const fields = {
    type: "image.actor" as const,
    model: model.id,
    gender: draft.gender,
    prompt: null,
    builder: builderOf(draft),
    outputs: place.outputs ?? Math.min(outputs, most),
    references: [],
  };
  const quote = ImageActorQuote.safeParse(fields);
  const request = ImageActorRequest.safeParse(fields);
  const job = usePricedStart({
    teamId,
    brandId,
    quote: quote.success ? quote.data : undefined,
    request: request.success ? request.data : undefined,
    onStarted,
  });
  const ageAt = draft.age === null ? 0 : AGES.findIndex((each) => each.value === draft.age);
  return (
    <GlassPanel
      inline={inline}
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      summary={<span>AI Actor Builder</span>}
      above={above}
      footer={
        <Group justify="flex-end" wrap="nowrap" gap={12}>
          <ModelPicker models={models} value={model.id} onChange={setModelId} />
          <OutputsChoice
            place={place}
            most={most}
            value={Math.min(outputs, most)}
            onChange={setOutputs}
          />
          <Price priced={job.priced} pending={job.pricing} />
          <StartActions
            place={place}
            canStart={job.canStart}
            starting={job.start.isPending}
            onStart={job.startJob}
          />
        </Group>
      }
    >
      <ScrollArea.Autosize mah={400} type="hover" scrollbarSize={4}>
        <div className={classes.builder}>
          <Stack gap={24}>
            <Text className={classes.section}>Character and style</Text>
            <Stack gap={8}>
              <Label>Gender</Label>
              <GenderPills value={draft.gender} onChange={(gender) => set({ gender })} />
            </Stack>
            <Stack gap={8}>
              <Label>Age</Label>
              <Slider
                aria-label="Age"
                min={0}
                max={AGES.length - 1}
                step={1}
                value={ageAt}
                label={null}
                marks={AGES.map((each, at) => ({ value: at, label: each.label }))}
                onChange={(at) => set({ age: AGES[at]?.value ?? null })}
                mb="lg"
              />
            </Stack>
            <Stack gap={8}>
              <Select
                label={<Label>Ethnicity</Label>}
                placeholder="Select ethnicity"
                clearable
                data={ETHNICITIES}
                value={draft.ethnicity}
                onChange={(value) =>
                  set({ ethnicity: (value as AiActorEthnicity | null) ?? null, ethnicityOther: "" })
                }
              />
              {draft.ethnicity === "other" ? (
                <TextInput
                  label="Enter your answer"
                  value={draft.ethnicityOther}
                  onChange={(event) => set({ ethnicityOther: event.currentTarget.value })}
                />
              ) : null}
            </Stack>
            <Stack gap={8}>
              <Label>Body Type</Label>
              <Cards label="Body Type" options={BODIES} value={draft.body} onChange={(body) => set({ body })} />
            </Stack>
            <Stack gap={8}>
              <Label>Face</Label>
              <Cards label="Face" options={FACES} value={draft.face} onChange={(face) => set({ face })} />
            </Stack>
            <Stack gap={8}>
              <Label>Eyes</Label>
              <Colors
                label="Eyes"
                token="eye"
                options={EYES}
                value={draft.eyes}
                custom={draft.eyesCustom}
                onChange={(eyes) => set({ eyes, eyesCustom: null })}
                onCustom={(hex) => set({ eyes: "custom", eyesCustom: hex })}
              />
            </Stack>
            <Stack gap={8}>
              <Label>Hair</Label>
              <Group gap={16} wrap="nowrap" align="center">
                <Select
                  aria-label="Hair length"
                  placeholder="Short"
                  data={HAIR_LENGTHS}
                  value={draft.hairLength}
                  onChange={(value) => set({ hairLength: (value as AiActorHairLength | null) ?? null })}
                  w={110}
                />
                <Colors
                  label="Hair"
                  token="hair"
                  options={HAIR_COLORS}
                  value={draft.hairColor}
                  custom={draft.hairColorCustom}
                  onChange={(hairColor) => set({ hairColor, hairColorCustom: null })}
                  onCustom={(hex) => set({ hairColor: "custom", hairColorCustom: hex })}
                />
              </Group>
            </Stack>
            <Stack gap={8}>
              <Label>Clothing</Label>
              <Cards
                label="Clothing"
                options={CLOTHING}
                value={draft.clothing}
                onChange={(clothing) => set({ clothing })}
              />
            </Stack>
          </Stack>
          <Stack gap={24}>
            <Text className={classes.section}>Scene and style</Text>
            <Stack gap={8}>
              <Label>Framing &amp; Angle</Label>
              <Cards
                label="Framing"
                options={FRAMINGS}
                value={draft.framing}
                onChange={(framing) => set({ framing })}
              />
              <Cards label="Angle" options={ANGLES} value={draft.angle} onChange={(angle) => set({ angle })} />
            </Stack>
            <Stack gap={8}>
              <Label>In Seat</Label>
              <Cards
                label="In Seat"
                options={SEATS}
                value={draft.seated === null ? null : draft.seated ? "yes" : "no"}
                onChange={(seat) => set({ seated: seat === null ? null : seat === "yes" })}
              />
            </Stack>
            <Stack gap={8}>
              <Select
                label={<Label>Lighting</Label>}
                placeholder="Select lighting"
                clearable
                data={LIGHTINGS}
                value={draft.lighting}
                onChange={(value) =>
                  set({ lighting: (value as AiActorLighting | null) ?? null, lightingOther: "" })
                }
              />
              {draft.lighting === "other" ? (
                <TextInput
                  label="Enter your answer"
                  value={draft.lightingOther}
                  onChange={(event) => set({ lightingOther: event.currentTarget.value })}
                />
              ) : null}
            </Stack>
            <Stack gap={8}>
              <Label>Background</Label>
              <Cards
                label="Background"
                options={BACKGROUNDS}
                value={draft.background}
                onChange={(background) => set({ background })}
              />
            </Stack>
            <Textarea
              label={<Label>Description</Label>}
              placeholder="Describe Background..."
              minRows={3}
              autosize
              maxLength={2000}
              value={draft.description}
              onChange={(event) => set({ description: event.currentTarget.value })}
            />
          </Stack>
        </div>
      </ScrollArea.Autosize>
      {job.quoteError === null ? null : <ErrorAlert error={job.quoteError} />}
      {job.start.isError ? <ErrorAlert error={job.start.error} /> : null}
    </GlassPanel>
  );
}
